import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Input, Textarea } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { TabBar, TabPanel } from '@/ui/TabBar/TabBar'
import { skillsApi } from '../../api/skills'
import { RefreshFailed } from '../../components/RefreshFailed'
import { skillsCopy } from '../../copy'
import { useMutationToast } from '../../hooks/useMutationToast'
import type { SkillDetail, SkillSummary } from '../../types/api'
import { formatDate } from '../../utils/time'
import styles from './SkillsPanel.module.css'
import { type SkillForm, skillFormToUpdateBody, skillToForm } from './skillForm'

/**
 * Skills browser and editor.
 *
 * Skills were fully implemented on the backend — tools, storage, and a curator
 * that archives agent-authored skills after 30 days unread — with no way for a
 * human to see them. The archived view matters as much as the active one: the
 * curator removes work without asking, so restoring has to be possible.
 */
export function SkillsPanel({
  wsId,
  canManage,
}: {
  wsId: string
  canManage: boolean
}) {
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useState<'active' | 'archived'>('active')
  const [openName, setOpenName] = useState<string | null>(null)
  const isCreatingParam = canManage && searchParams.get('create') === 'true'
  const [creatingManual, setCreatingManual] = useState(false)
  const creating = isCreatingParam || creatingManual

  function stopCreating() {
    setCreatingManual(false)
    if (searchParams.get('create')) {
      const next = new URLSearchParams(searchParams)
      next.delete('create')
      setSearchParams(next, { replace: true })
    }
  }

  const archived = view === 'archived'
  const { data, isLoading, isError, refetch } = useQuery<SkillSummary[]>({
    queryKey: ['skills', wsId, view],
    queryFn: () => skillsApi.list(wsId, { archived }),
    enabled: !!wsId,
    staleTime: 15_000,
  })

  const showSkeleton = useSkeletonDelay(isLoading)
  const editorId = useId()
  const skills = data ?? []

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['skills', wsId] })
  }

  const unarchiveMut = useMutationToast({
    mutationFn: (name: string) => skillsApi.unarchive(wsId, name),
    successMessage: skillsCopy.toast.restored,
    errorMessage: skillsCopy.toast.restoreFailed,
    onSuccess: invalidate,
  })

  const showCreate = !archived && canManage
  const newSkill = (
    <Button variant="ghost" onClick={() => setCreatingManual(true)}>
      <Icon name="plus" size="icon-embedded" />
      {skillsCopy.actions.newSkill}
    </Button>
  )
  const createRow = showCreate && (
    <RowListItem className={creating ? styles.form : undefined}>
      {creating ? (
        <CreateSkillForm
          wsId={wsId}
          onCancel={stopCreating}
          onCreated={(name) => {
            stopCreating()
            setOpenName(name)
            invalidate()
          }}
        />
      ) : (
        newSkill
      )}
    </RowListItem>
  )

  const body = (
    <Stack gap="space-12">
      {isError && data && (
        <RefreshFailed
          message={skillsCopy.refreshFailed}
          onRetry={() => void refetch()}
        />
      )}
      {isLoading ? (
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      ) : isError && !data ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline" wrap>
            <Text>{skillsCopy.loadFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {skillsCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : skills.length === 0 && archived ? (
        <EmptyState variant="inline" description={skillsCopy.empty.archived} />
      ) : skills.length === 0 && !creating ? (
        <EmptyState
          title={skillsCopy.empty.active}
          titleAs="h2"
          action={showCreate ? newSkill : undefined}
        />
      ) : (
        <RowList>
          {skills.map((s) => {
            const builtin = s.id.startsWith('builtin:')
            const open = openName === s.name
            const meta = skillsCopy.meta({
              builtin,
              uses: s.use_count,
              lastRead: s.last_viewed_at ? formatDate(s.last_viewed_at) : null,
              tags: s.tags,
            })
            return (
              <RowListItem key={s.id}>
                <Text tone="text-ghost" className={styles.glyph}>
                  {s.author === 'system' ? (
                    <Icon
                      name="gear"
                      size="icon-embedded"
                      label={skillsCopy.glyph.builtin}
                    />
                  ) : s.author === 'agent' ? (
                    <Icon
                      name="cube"
                      size="icon-embedded"
                      label={skillsCopy.glyph.agent}
                    />
                  ) : (
                    <Icon
                      name="person"
                      size="icon-embedded"
                      label={skillsCopy.glyph.person}
                    />
                  )}
                </Text>
                <Stack gap="space-4" className={styles.main}>
                  <Inline gap="space-8">
                    <Text tone="text-sub">{s.name}</Text>
                    {builtin && (
                      <Chip variant="kind" label={skillsCopy.builtinChip} />
                    )}
                  </Inline>
                  <Text variant="caption" tone="text-mute">
                    {s.description || skillsCopy.noDescription}
                  </Text>
                  {meta && (
                    <Text variant="caption" tone="text-mute" numeric>
                      {meta}
                    </Text>
                  )}
                </Stack>
                <Inline gap="space-4" wrap={false} className={styles.actions}>
                  {archived && canManage && (
                    <Button
                      variant="ghost"
                      loading={
                        unarchiveMut.isPending &&
                        unarchiveMut.variables === s.name
                      }
                      onClick={() => unarchiveMut.mutate(s.name)}
                    >
                      {skillsCopy.actions.restore}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    aria-expanded={open}
                    aria-controls={open ? editorId : undefined}
                    aria-label={skillsCopy.toggleName(open, s.name)}
                    onClick={() => setOpenName(open ? null : s.name)}
                  >
                    {open ? skillsCopy.actions.close : skillsCopy.actions.open}
                  </Button>
                </Inline>
              </RowListItem>
            )
          })}
          {createRow}
        </RowList>
      )}

      {openName && (
        <SkillEditor
          id={editorId}
          wsId={wsId}
          name={openName}
          canManage={canManage}
          onClose={() => setOpenName(null)}
          onChanged={invalidate}
        />
      )}
    </Stack>
  )

  return (
    <section>
      <TabBar
        label={skillsCopy.tabs.label}
        tabs={[
          { id: 'active', label: skillsCopy.tabs.active },
          { id: 'archived', label: skillsCopy.tabs.archived },
        ]}
        active={view}
        onChange={(id) => {
          setView(id as 'active' | 'archived')
          setOpenName(null)
          stopCreating()
        }}
      >
        <TabPanel id="active">{body}</TabPanel>
        <TabPanel id="archived">{body}</TabPanel>
      </TabBar>
    </section>
  )
}

function CreateSkillForm({
  wsId,
  onCancel,
  onCreated,
}: {
  wsId: string
  onCancel: () => void
  onCreated: (name: string) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const createMut = useMutationToast({
    mutationFn: () =>
      skillsApi.create(wsId, { name: name.trim(), description, content: '' }),
    successMessage: skillsCopy.toast.created,
    errorMessage: skillsCopy.toast.createFailed,
    onSuccess: () => onCreated(name.trim()),
  })

  return (
    <form
      aria-label={skillsCopy.actions.newSkill}
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) createMut.mutate()
      }}
    >
      <Stack gap="space-12">
        <Field label={skillsCopy.create.nameLabel}>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={skillsCopy.create.namePlaceholder}
          />
        </Field>
        <Field label={skillsCopy.create.descriptionLabel}>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Inline gap="space-8">
          <Button
            type="submit"
            disabled={!name.trim()}
            loading={createMut.isPending}
          >
            {skillsCopy.actions.create}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            {skillsCopy.actions.cancel}
          </Button>
        </Inline>
      </Stack>
    </form>
  )
}

function SkillEditor({
  id,
  wsId,
  name,
  canManage,
  onClose,
  onChanged,
}: {
  id: string
  wsId: string
  name: string
  canManage: boolean
  onClose: () => void
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deletingFile, setDeletingFile] = useState<string | null>(null)
  // The form is the fetched skill until the user types; `draft` is the overlay.
  // Deriving rather than seeding via an effect means there is no render where
  // the fields are empty, and a refetch can't clobber unsaved edits.
  const [draft, setDraft] = useState<SkillForm | null>(null)

  const {
    data: skill,
    isLoading,
    isError,
    refetch,
  } = useQuery<SkillDetail>({
    queryKey: ['skill', wsId, name],
    queryFn: () => skillsApi.get(wsId, name),
  })
  const shown = useSkeletonDelay(isLoading)

  function invalidateSkill() {
    void qc.invalidateQueries({ queryKey: ['skill', wsId, name] })
    onChanged()
  }

  const saved = skillToForm(skill)
  const form = draft ?? saved

  const saveMut = useMutationToast({
    mutationFn: () => skillsApi.update(wsId, name, skillFormToUpdateBody(form)),
    successMessage: skillsCopy.toast.saved,
    errorMessage: skillsCopy.toast.saveFailed,
    onSuccess: () => {
      // Drop the overlay so the form follows the refetched skill again.
      setDraft(null)
      invalidateSkill()
    },
  })

  const deleteMut = useMutationToast({
    mutationFn: () => skillsApi.remove(wsId, name),
    successMessage: skillsCopy.toast.deleted,
    errorMessage: skillsCopy.toast.deleteFailed,
    onSuccess: () => {
      setConfirmingDelete(false)
      onClose()
      onChanged()
    },
  })

  const deleteFileMut = useMutationToast({
    mutationFn: (path: string) => skillsApi.removeFile(wsId, name, path),
    successMessage: skillsCopy.toast.fileDeleted,
    errorMessage: skillsCopy.toast.fileDeleteFailed,
    onSuccess: () => {
      setDeletingFile(null)
      invalidateSkill()
    },
  })

  if (isLoading) {
    return (
      <div id={id} aria-busy="true">
        {shown && <Skeleton lines={4} />}
      </div>
    )
  }
  if (!skill) {
    return (
      <div id={id}>
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline" wrap>
            <Text>{skillsCopy.loadSkillFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {skillsCopy.retry}
            </Button>
          </Inline>
        </Alert>
      </div>
    )
  }

  const isBuiltin = skill.id.startsWith('builtin:')
  const editable = canManage && !isBuiltin

  const dirty =
    form.description !== saved.description ||
    form.content !== saved.content ||
    form.tags !== saved.tags

  return (
    <Card variant="flat" id={id}>
      <Stack gap="space-12">
        {isError && (
          <RefreshFailed
            message={skillsCopy.refreshSkillFailed}
            onRetry={() => void refetch()}
          />
        )}
        <Inline gap="space-8" justify="between">
          <SectionHeader as="h2">{skill.name}</SectionHeader>
          <Text variant="caption" tone="text-ghost" numeric>
            {skillsCopy.editor.meta({
              builtin: isBuiltin,
              version: skill.version,
              author: skill.author,
              updated: formatDate(skill.updated_at),
            })}
          </Text>
        </Inline>

        <Field label={skillsCopy.editor.description} readOnly={!editable}>
          <Input
            value={form.description}
            onChange={(e) => setDraft({ ...form, description: e.target.value })}
          />
        </Field>

        <Field label={skillsCopy.editor.tags} readOnly={!editable}>
          <Input
            value={form.tags}
            onChange={(e) => setDraft({ ...form, tags: e.target.value })}
          />
        </Field>

        <Field label={skillsCopy.editor.content} readOnly={!editable}>
          <Textarea
            value={form.content}
            onChange={(e) => setDraft({ ...form, content: e.target.value })}
            rows={16}
            mono
          />
        </Field>

        {skill.files.length > 0 && (
          <Stack gap="space-4">
            <Text variant="caption" tone="text-mute">
              {skillsCopy.editor.supportingFiles}
            </Text>
            <RowList>
              {skill.files.map((f) => (
                <RowListItem key={f.path}>
                  <Icon name="file" size="icon-embedded" />
                  <Text variant="code" tone="text-sub" className={styles.main}>
                    {f.path}
                  </Text>
                  <Text variant="caption" tone="text-ghost" numeric>
                    {skillsCopy.editor.bytes(f.content.length)}
                  </Text>
                  {editable && (
                    <Button
                      variant="ghost"
                      aria-label={skillsCopy.deleteFileName(f.path)}
                      onClick={() => setDeletingFile(f.path)}
                    >
                      {skillsCopy.actions.delete}
                    </Button>
                  )}
                </RowListItem>
              ))}
            </RowList>
          </Stack>
        )}

        {isBuiltin && (
          <Text as="p" variant="caption" tone="text-mute">
            {skillsCopy.editor.builtinNotice}
          </Text>
        )}

        {editable ? (
          <Inline gap="space-8">
            <Button
              disabled={!dirty}
              onClick={() => saveMut.mutate()}
              loading={saveMut.isPending}
            >
              {skillsCopy.actions.save}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {skillsCopy.actions.close}
            </Button>
            <Button variant="outline" onClick={() => setConfirmingDelete(true)}>
              {skillsCopy.actions.deleteSkill}
            </Button>
          </Inline>
        ) : (
          <Inline gap="space-8">
            <Button variant="ghost" onClick={onClose}>
              {skillsCopy.actions.close}
            </Button>
          </Inline>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmingDelete}
        title={skillsCopy.confirm.skillTitle}
        tone="danger"
        description={skillsCopy.confirm.skillDescription(skill.name)}
        confirmLabel={skillsCopy.confirm.confirm}
        loading={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onClose={() => setConfirmingDelete(false)}
      />

      <ConfirmDialog
        open={deletingFile !== null}
        title={skillsCopy.confirm.fileTitle}
        tone="danger"
        description={
          deletingFile
            ? skillsCopy.confirm.fileDescription(deletingFile)
            : undefined
        }
        confirmLabel={skillsCopy.confirm.confirm}
        loading={deleteFileMut.isPending}
        onConfirm={() => {
          if (deletingFile) deleteFileMut.mutate(deletingFile)
        }}
        onClose={() => setDeletingFile(null)}
      />
    </Card>
  )
}
